import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';

const COLORS = ['#3B82F6', '#F59E0B', '#8B5CF6', '#22C55E', '#EC4899', '#EAB308'];

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function calcDurationMins(start, end) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  return mins;
}

function crossesMidnight(start, end) {
  if (!start || !end) return false;
  return end <= start;
}

function durationPreview(start, end) {
  if (!start || !end || start === end) return null;
  const mins = calcDurationMins(start, end);
  const h = mins / 60;
  const hours = Number.isInteger(h) ? String(h) : h.toFixed(1);
  return { hours, crosses: crossesMidnight(start, end) };
}

function toTimeValue(t) {
  return t && t.split(':').length === 2 ? `${t}:00` : t;
}

function ShiftTypeModal({ open, onClose, onSaved, existing }) {
  const { t } = useTranslation('shiftTypes');
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [colorHex, setColorHex] = useState(COLORS[0]);
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.label || existing.name || existing.shiftName || '');
      setStartTime((existing.startTime || '').slice(0, 5));
      setEndTime((existing.endTime || '').slice(0, 5));
      setColorHex(existing.colorHex || COLORS[0]);
    } else {
      setName('');
      setStartTime('');
      setEndTime('');
      setColorHex(COLORS[0]);
    }
    setNameError('');
    setSaving(false);
    setTimeout(() => nameRef.current?.focus(), 50);
  }, [open, existing]);

  const isValid = name.trim().length > 0 && startTime && endTime && startTime !== endTime;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setNameError('');
    try {
      let res;
      if (!existing) {
        res = await apiClient.post('/api/shift-templates', {
          label: name.trim(),
          startTime: toTimeValue(startTime),
          endTime: toTimeValue(endTime),
          colorHex,
        });
      } else {
        const patch = {};
        const existingLabel = existing.label || existing.name || existing.shiftName || '';
        if (name.trim() !== existingLabel) patch.label = name.trim();
        if (startTime !== (existing.startTime || '').slice(0, 5)) patch.startTime = toTimeValue(startTime);
        if (endTime !== (existing.endTime || '').slice(0, 5)) patch.endTime = toTimeValue(endTime);
        if (colorHex !== existing.colorHex) patch.colorHex = colorHex;
        if (Object.keys(patch).length === 0) { onClose(); return; }
        res = await apiClient.patch(`/api/shift-templates/${existing.id}`, patch);
      }
      onSaved(res.data);
      onClose();
    } catch (err) {
      const status = err.response?.status;
      const msgKey = err.response?.data?.messageKey;
      if (status === 409 && msgKey === 'error.shifttemplate.label.exists') {
        setNameError(err.response.data.message || 'A shift type with this name already exists.');
      } else {
        setNameError(err.response?.data?.message || 'Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const preview = durationPreview(startTime, endTime);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="st-modal-title"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-orange-100 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 id="st-modal-title" className="text-lg font-semibold text-gray-900">
            {existing ? t('modal.editTitle') : t('modal.addTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="ml-4 text-gray-400 hover:text-gray-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label htmlFor="st-name" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('modal.fields.name')} <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              ref={nameRef}
              id="st-name"
              type="text"
              maxLength={50}
              value={name}
              onChange={e => { setName(e.target.value); setNameError(''); }}
              placeholder={t('modal.fields.namePlaceholder')}
              disabled={saving}
              className={`w-full px-3.5 py-2.5 rounded-lg border bg-white text-gray-900 placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-orange-200 disabled:opacity-60 ${
                nameError ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-orange-500'
              }`}
            />
            {nameError && (
              <p className="mt-1.5 text-xs text-red-600" role="alert">{nameError}</p>
            )}
          </div>

          {/* Time fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="st-start" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('modal.fields.startsAt')} <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                id="st-start"
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                disabled={saving}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
              />
            </div>
            <div>
              <label htmlFor="st-end" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('modal.fields.endsAt')} <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                id="st-end"
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                disabled={saving}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
              />
            </div>
          </div>

          {/* Duration preview */}
          {preview && (
            <p className="text-xs text-gray-500 -mt-1">
              {preview.crosses
                ? t('durationHoursCrossesMidnight', { hours: preview.hours })
                : t('durationHours', { hours: preview.hours })}
            </p>
          )}

          {/* Color picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('modal.fields.color')}
            </label>
            <div className="flex items-center gap-2.5">
              {COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColorHex(c)}
                  disabled={saving}
                  className="rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-1 disabled:opacity-60 transition-transform hover:scale-110"
                  style={{
                    width: 22,
                    height: 22,
                    backgroundColor: c,
                    boxShadow: colorHex === c
                      ? `0 0 0 2px #fff, 0 0 0 4px ${c}`
                      : '0 0 0 1px rgba(0,0,0,0.12)',
                  }}
                  aria-label={c}
                  aria-pressed={colorHex === c}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            {t('modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || saving}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Spinner />}
            {t('modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShiftTypeModal;